import {
	DisciplineEnum,
	DisciplineOptions,
	FrequencyCadenceTypeEnum,
	FrequencyStatusEnum,
	FrequencyTypeEnum,
	VisitTypeEnum,
	type CertificationPeriodViewModel,
	type FrequencyViewModel,
	type FullOrderViewModel,
	type LastEvaluationStatusesViewModel,
	type SearchFrequencyViewModel,
	type UserViewModel
} from '@generated';
import { createNewEvaluationFrequency, createNewRoutineFrequency } from '../util';
import type { IsSameCadenceCorrelatorFunc } from '../useCadenceCorrelator';
import type { DraftEvaluationFrequencyModel, DraftPrnRoutineFrequencyModel, DraftRoutineFrequencyModel, SuggestedFrequenciesModel } from '../types';

export type SelectedFrequencyView = 'calendar' | 'list' | 'prn';

export type WizardAction =
	| { type: 'SET_SELECTED_FREQUENCY_VIEW'; newSelectedView: SelectedFrequencyView }
	| { type: 'SET_SELECTED_DISCIPLINE_ID'; disciplineId: number }
	| { type: 'SET_SELECTED_CADENCE_TYPE_ID'; frequencyCadenceTypeId: number }
	| { type: 'TOGGLE_ASSIGN_TO_ME' }
	| { type: 'CALENDAR_CELL_CLICKED'; selectedDate: Date }
	| { type: 'DRAFT_CALENDAR_EVENT_CLICKED'; frequencyId: string }
	| { type: 'DRAFT_DISCONTINUED_CALENDAR_EVENT_CLICKED'; frequencyId: string }
	| { type: 'EXISTING_CALENDAR_EVENT_CLICKED'; frequencyId: string }
	| { type: 'LIST_UPDATE_DRAFT_FREQUENCY'; frequencyId: string; scheduledDateAndTime: Date; assignedToId?: string }
	| { type: 'LIST_REMOVE_DRAFT_FREQUENCY'; frequencyId: string }
	| { type: 'REMOVE_DRAFT_PRN_FREQUENCY'; frequencyId: string }
	| { type: 'ADD_DRAFT_PRN_FREQUENCY' };

export type WizardState = {
	loggedInUser: UserViewModel;
	selectedFrequencyView: SelectedFrequencyView;
	selectedDisciplineId: number;
	selectedCadenceTypeId: number;
	displayAssignToMeCheckbox: boolean;
	assignCurrentUserToNewFrequencies: boolean;
	unevaluatedDisciplineIds: number[];

	draftEvaluations: DraftEvaluationFrequencyModel[];
	draftRoutineFrequencies: DraftRoutineFrequencyModel[];
	draftPrnFrequencies: DraftPrnRoutineFrequencyModel[];
	discontinuedFrequencies: (SearchFrequencyViewModel | FrequencyViewModel)[];
	discontinuedPrnFrequencyIds: string[];

	existingFrequencies: (SearchFrequencyViewModel | FrequencyViewModel)[];

	certificationPeriod: CertificationPeriodViewModel;
	isSameCadenceCorrelatorFunc: IsSameCadenceCorrelatorFunc;

	frequencyDiscontinueError: false | string;
};

export interface WizardReducer {
	wizardState: WizardState;
	dispatch: React.Dispatch<WizardAction>;
}

export function getInitialWizardState(
	loggedInUser: UserViewModel,
	lastEvaluationStatuses: LastEvaluationStatusesViewModel,
	existingFrequencies: SearchFrequencyViewModel[],
	order: FullOrderViewModel,
	isSameCadenceCorrelatorFunc: IsSameCadenceCorrelatorFunc,
	suggestedFrequencies?: SuggestedFrequenciesModel
): WizardState {
	const pendingOrOnServiceDisciplines = lastEvaluationStatuses.statuses.filter(x => x.hasPendingEvaluation || x.isOnService);
	const unevaluatedDisciplineIds = DisciplineOptions.filter(x => !pendingOrOnServiceDisciplines.some(d => d.disciplineId === x.id)).map(x => x.id);

	return {
		loggedInUser,
		selectedFrequencyView: 'calendar',
		selectedDisciplineId: loggedInUser.clinicalTypeDisciplineId ? loggedInUser.clinicalTypeDisciplineId : DisciplineOptions[0].id,
		selectedCadenceTypeId: FrequencyCadenceTypeEnum.Weekly,
		assignCurrentUserToNewFrequencies: loggedInUser.clinicalTypeId ? true : false,
		displayAssignToMeCheckbox: loggedInUser.clinicalTypeId ? true : false,
		draftEvaluations: [
			...order.evaluations,
			...(suggestedFrequencies?.evaluations ? suggestedFrequencies.evaluations.map(x => ({ ...x, id: crypto.randomUUID() })) : [])
		],
		draftRoutineFrequencies: [
			...order.routine.map(x => ({ ...x, id: crypto.randomUUID() })),
			...(suggestedFrequencies?.routineFrequencies ? suggestedFrequencies.routineFrequencies.map(x => ({ ...x, id: crypto.randomUUID() })) : [])
		],
		draftPrnFrequencies: [
			...order.routinePrnFrequencies,
			...(suggestedFrequencies?.prnFrequencies ? suggestedFrequencies.prnFrequencies.map(x => ({ ...x, id: crypto.randomUUID() })) : [])
		],
		discontinuedFrequencies: order.discontinuedFrequencies,
		discontinuedPrnFrequencyIds: order.discontinuedRoutinePrnFrequencies.map(f => f.id),
		unevaluatedDisciplineIds: unevaluatedDisciplineIds,
		existingFrequencies,
		certificationPeriod: order.certificationPeriod,
		isSameCadenceCorrelatorFunc,
		frequencyDiscontinueError: false
	};
}

function getAssignedToId(wizardState: WizardState) {
	return wizardState.assignCurrentUserToNewFrequencies && wizardState.selectedDisciplineId === wizardState.loggedInUser.clinicalTypeDisciplineId
		? wizardState.loggedInUser.id
		: undefined;
}

function draftDiscontinuedFrequencyExists(state: WizardState, frequencyTypeId: number, selectedDate: Date) {
	return (
		state.discontinuedFrequencies.filter(
			x =>
				x.frequencyTypeId === frequencyTypeId &&
				x.frequencyCadenceTypeId === state.selectedCadenceTypeId &&
				x.disciplineId === state.selectedDisciplineId &&
				state.isSameCadenceCorrelatorFunc(state.selectedCadenceTypeId, x.scheduledDateAndTime, selectedDate)
		).length > 0
	);
}

function draftFrequencyExists(state: WizardState, existingFrequency: SearchFrequencyViewModel | FrequencyViewModel) {
	if (existingFrequency.frequencyTypeId === FrequencyTypeEnum.Evaluation) {
		return state.draftEvaluations.some(x => x.disciplineId === existingFrequency.disciplineId);
	}
	return state.draftRoutineFrequencies.some(
		x =>
			x.disciplineId === existingFrequency.disciplineId &&
			x.frequencyCadenceTypeId === existingFrequency.frequencyCadenceTypeId &&
			state.isSameCadenceCorrelatorFunc(existingFrequency.frequencyCadenceTypeId, x.scheduledDateAndTime, existingFrequency.scheduledDateAndTime)
	);
}

const FREQUENCY_DISCONTINUE_ERROR_MESSAGE = 'Unable to add a new visit when a discontinued frequency of the same period exists on order.';
const FREQUENCY_DISCONTINUE_ERROR_MESSAGE_ON_EXISTING = 'Unable to discontinue frequency when a new frequency exists for the same period on order';
export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'SET_SELECTED_FREQUENCY_VIEW':
			return { ...state, selectedFrequencyView: action.newSelectedView };
		case 'SET_SELECTED_DISCIPLINE_ID':
			return { ...state, selectedDisciplineId: action.disciplineId };
		case 'SET_SELECTED_CADENCE_TYPE_ID':
			return { ...state, selectedCadenceTypeId: action.frequencyCadenceTypeId };
		case 'TOGGLE_ASSIGN_TO_ME':
			return { ...state, assignCurrentUserToNewFrequencies: !state.assignCurrentUserToNewFrequencies };
		case 'CALENDAR_CELL_CLICKED': {
			const requiresEvaluation =
				state.selectedDisciplineId !== DisciplineEnum.HomeHealthAide &&
				state.unevaluatedDisciplineIds.some(d => d === state.selectedDisciplineId);

			if (!requiresEvaluation) {
				if (draftDiscontinuedFrequencyExists(state, FrequencyTypeEnum.Routine, action.selectedDate)) {
					return {
						...state,
						frequencyDiscontinueError: FREQUENCY_DISCONTINUE_ERROR_MESSAGE
					};
				}
				const newDraftRevisit = createNewRoutineFrequency(
					action.selectedDate,
					state.selectedCadenceTypeId,
					state.selectedDisciplineId,
					getAssignedToId(state)
				);
				return {
					...state,
					draftRoutineFrequencies: [...state.draftRoutineFrequencies, newDraftRevisit],
					frequencyDiscontinueError: false
				};
			}

			const existingEvaluation = state.draftEvaluations.find(x => x.disciplineId === state.selectedDisciplineId);

			if (!existingEvaluation) {
				if (draftDiscontinuedFrequencyExists(state, FrequencyTypeEnum.Evaluation, action.selectedDate)) {
					return {
						...state,
						frequencyDiscontinueError: FREQUENCY_DISCONTINUE_ERROR_MESSAGE
					};
				}
				const newDraftEvaluation = createNewEvaluationFrequency(action.selectedDate, state.selectedDisciplineId, getAssignedToId(state));
				return {
					...state,
					draftEvaluations: [...state.draftEvaluations, newDraftEvaluation],
					frequencyDiscontinueError: false
				};
			}
			if (existingEvaluation.scheduledDateAndTime.getTime() < action.selectedDate.getTime()) {
				console.log('Existing evaluation is before the selected date, creating new routine frequency');
				if (draftDiscontinuedFrequencyExists(state, FrequencyTypeEnum.Routine, action.selectedDate)) {
					return {
						...state,
						frequencyDiscontinueError: FREQUENCY_DISCONTINUE_ERROR_MESSAGE
					};
				}
				const newDraftRevisit = createNewRoutineFrequency(
					action.selectedDate,
					state.selectedCadenceTypeId,
					state.selectedDisciplineId,
					getAssignedToId(state)
				);
				return {
					...state,
					draftRoutineFrequencies: [...state.draftRoutineFrequencies, newDraftRevisit],
					frequencyDiscontinueError: false
				};
			}

			return {
				...state,
				draftEvaluations: [
					...state.draftEvaluations.filter(x => x.disciplineId !== state.selectedDisciplineId),
					createNewEvaluationFrequency(action.selectedDate, state.selectedDisciplineId, getAssignedToId(state))
				],
				draftRoutineFrequencies: [
					...state.draftRoutineFrequencies,
					createNewRoutineFrequency(
						existingEvaluation.scheduledDateAndTime,
						state.selectedCadenceTypeId,
						state.selectedDisciplineId,
						getAssignedToId(state)
					)
				],
				frequencyDiscontinueError: false
			};
		}
		case 'DRAFT_CALENDAR_EVENT_CLICKED': {
			const routineDraftFrequency = state.draftRoutineFrequencies.find(x => x.id === action.frequencyId);
			if (routineDraftFrequency) {
				return {
					...state,
					draftRoutineFrequencies: state.draftRoutineFrequencies.filter(x => x.id !== action.frequencyId),
					frequencyDiscontinueError: false
				};
			}
			const draftEvaluation = state.draftEvaluations.find(x => x.id === action.frequencyId);
			if (!draftEvaluation) {
				throw new Error(`Draft evaluation with ID ${action.frequencyId} not found`);
			}

			const correspondingRoutineFrequencies = state.draftRoutineFrequencies.filter(x => x.disciplineId === draftEvaluation.disciplineId);

			if (correspondingRoutineFrequencies.length === 0) {
				return {
					...state,
					draftEvaluations: state.draftEvaluations.filter(x => x.id !== action.frequencyId),
					frequencyDiscontinueError: false
				};
			}

			const earliestRoutineFrequency = correspondingRoutineFrequencies.sort(
				(a, b) => a.scheduledDateAndTime.getTime() - b.scheduledDateAndTime.getTime()
			)[0];

			return {
				...state,
				draftEvaluations: [
					...state.draftEvaluations.filter(x => x.id !== action.frequencyId),
					createNewEvaluationFrequency(earliestRoutineFrequency.scheduledDateAndTime, draftEvaluation.disciplineId, draftEvaluation.assignedToId)
				],
				draftRoutineFrequencies: state.draftRoutineFrequencies.filter(x => x.id !== earliestRoutineFrequency.id),
				frequencyDiscontinueError: FREQUENCY_DISCONTINUE_ERROR_MESSAGE_ON_EXISTING
			};
		}
		case 'EXISTING_CALENDAR_EVENT_CLICKED': {
			const existingFrequency = state.existingFrequencies.find(
				(x: SearchFrequencyViewModel | FrequencyViewModel) => x.id === action.frequencyId
			);
			if (!existingFrequency) {
				throw new Error(`Frequency with ID ${action.frequencyId} not found`);
			} else if (!existingFrequency.invariants.canBeDiscontinued || state.discontinuedFrequencies.some(x => x.id === action.frequencyId)) {
				return state;
			}

			if (draftFrequencyExists(state, existingFrequency)) {
				return {
					...state,
					frequencyDiscontinueError: FREQUENCY_DISCONTINUE_ERROR_MESSAGE_ON_EXISTING
				};
			}

			return {
				...state,
				existingFrequencies: state.existingFrequencies.filter(
					(x: SearchFrequencyViewModel | FrequencyViewModel) => x.id !== action.frequencyId
				),
				discontinuedFrequencies: [
					...state.discontinuedFrequencies,
					{ ...existingFrequency, frequencyStatusId: FrequencyStatusEnum.DiscontinueDrafted }
				],
				frequencyDiscontinueError: false
			};
		}
		case 'DRAFT_DISCONTINUED_CALENDAR_EVENT_CLICKED': {
			const draftDiscontinue = state.discontinuedFrequencies.find(x => x.id === action.frequencyId);
			if (!draftDiscontinue) {
				throw new Error(`Frequency with ID ${action.frequencyId} is not discontinued`);
			}
			return {
				...state,
				existingFrequencies: [
					...state.existingFrequencies,
					{
						...draftDiscontinue,
						frequencyStatusId: draftDiscontinue.assignedToId ? FrequencyStatusEnum.NeedsPerformed : FrequencyStatusEnum.NeedsStaffed,
						invariants: { ...draftDiscontinue.invariants, canBeDiscontinued: true }
					}
				],
				discontinuedFrequencies: state.discontinuedFrequencies.filter(x => x.id !== action.frequencyId),
				frequencyDiscontinueError: false
			};
		}
		case 'LIST_UPDATE_DRAFT_FREQUENCY': {
			const { frequencyId, scheduledDateAndTime, assignedToId } = action;
			const existingEvaluation = state.draftEvaluations.find(x => x.id === frequencyId);
			if (existingEvaluation) {
				return {
					...state,
					draftEvaluations: [
						...state.draftEvaluations.filter(x => x.id !== frequencyId),
						{ ...existingEvaluation, scheduledDateAndTime, assignedToId }
					]
				};
			}
			const existingRoutineFrequency = state.draftRoutineFrequencies.find(x => x.id === frequencyId);
			if (!existingRoutineFrequency) {
				throw new Error(`Could not find draft frequency with id ${frequencyId}`);
			}
			return {
				...state,
				draftRoutineFrequencies: [
					...state.draftRoutineFrequencies.filter(x => x.id !== frequencyId),
					{ ...existingRoutineFrequency, scheduledDateAndTime, assignedToId }
				]
			};
		}
		case 'LIST_REMOVE_DRAFT_FREQUENCY': {
			const routineDraftFrequency = state.draftRoutineFrequencies.find(x => x.id === action.frequencyId);
			if (routineDraftFrequency) {
				return {
					...state,
					draftRoutineFrequencies: state.draftRoutineFrequencies.filter(x => x.id !== action.frequencyId),
					frequencyDiscontinueError: false
				};
			}
			const draftEvaluation = state.draftEvaluations.find(x => x.id === action.frequencyId);
			if (!draftEvaluation) {
				throw new Error(`Draft evaluation with ID ${action.frequencyId} not found`);
			}
			const correspondingRoutineFrequencies = state.draftRoutineFrequencies.filter(x => x.disciplineId === draftEvaluation.disciplineId);

			if (correspondingRoutineFrequencies.length === 0) {
				return {
					...state,
					draftEvaluations: state.draftEvaluations.filter(x => x.id !== action.frequencyId)
				};
			}

			const earliestRoutineFrequency = correspondingRoutineFrequencies.sort(
				(a, b) => a.scheduledDateAndTime.getTime() - b.scheduledDateAndTime.getTime()
			)[0];

			return {
				...state,
				draftEvaluations: [
					...state.draftEvaluations.filter(x => x.id !== action.frequencyId),
					createNewEvaluationFrequency(earliestRoutineFrequency.scheduledDateAndTime, draftEvaluation.disciplineId, draftEvaluation.assignedToId)
				],
				draftRoutineFrequencies: state.draftRoutineFrequencies.filter(x => x.id !== earliestRoutineFrequency.id),
				frequencyDiscontinueError: FREQUENCY_DISCONTINUE_ERROR_MESSAGE_ON_EXISTING
			};
		}
		case 'REMOVE_DRAFT_PRN_FREQUENCY': {
			const { frequencyId } = action;
			return {
				...state,
				draftPrnFrequencies: state.draftPrnFrequencies.filter(x => x.id !== frequencyId)
			};
		}
		case 'ADD_DRAFT_PRN_FREQUENCY': {
			return {
				...state,
				draftPrnFrequencies: [
					...state.draftPrnFrequencies,
					{
						id: crypto.randomUUID(),
						disciplineId: state.selectedDisciplineId,
						prnReason: '',
						count: 0,
						visitTypeId: VisitTypeEnum.InPerson
					}
				]
			};
		}

		default:
			return state;
	}
}
